import { Box, Checkbox, FormControlLabel, FormGroup, TextField, Typography } from "@mui/material";
import { Dialog } from "components/Dialog";
import { StaffPermission } from "generated-graphql/graphql";
import { StaffRoleRow } from "queries/getStaffRoles";
import { useState } from "react";

export type StaffRoleSaveInput = {
  name: string;
  description: string | null;
  permissions: StaffPermission[];
};

// Short, human-readable labels for the flat permission list (the enum values
// themselves aren't customer-facing copy).
const STAFF_PERMISSION_LABELS: Record<StaffPermission, string> = {
  [StaffPermission.RequeueJob]: "Re-queue failed jobs",
  [StaffPermission.ResetUnsafeReport]: "Reset unsafe reports",
  [StaffPermission.ManageWorkflowConfig]: "Manage workflow configs",
  [StaffPermission.ManageStaffRoles]: "Manage staff roles",
  [StaffPermission.ManageFeatureFlags]: "Manage feature flags",
  [StaffPermission.PauseStaffNotifications]: "Pause Encamp alerts for all staff",
  [StaffPermission.RunScoutEvalBenchmarks]: "Publish Scout evals and run benchmarks",
};

const ALL_STAFF_PERMISSIONS = Object.values(StaffPermission);

type StaffRoleEditorDialogProps = {
  /** The role being edited, or null when creating a new role. */
  role: StaffRoleRow | null;
  saving: boolean;
  onClose: () => void;
  onSave: (input: StaffRoleSaveInput) => void;
};

/**
 * Create/edit a staff role. Mounted fresh each open (rendered conditionally by
 * the parent), so form state initializes directly from the `role` prop.
 */
export function StaffRoleEditorDialog({
  role,
  saving,
  onClose,
  onSave,
}: StaffRoleEditorDialogProps) {
  const [name, setName] = useState(role?.name ?? "");
  const [description, setDescription] = useState(role?.description ?? "");
  const [permissions, setPermissions] = useState<StaffPermission[]>(role?.permissions ?? []);

  const trimmedName = name.trim();
  const isCreate = !role;

  const togglePermission = (permission: StaffPermission) => {
    setPermissions((prev) =>
      prev.includes(permission) ? prev.filter((p) => p !== permission) : [...prev, permission]
    );
  };

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="sm"
      title={isCreate ? "Create staff role" : "Edit staff role"}
      description="Staff roles grant permissions for sensitive staff actions, and apply to every staff user assigned the role."
      primaryActionButtonProps={{
        loading: saving,
        // Empty permissions is valid (e.g. the default "Standard" role), so the
        // only requirement is a name.
        disabled: !trimmedName,
        onClick: () =>
          onSave({
            name: trimmedName,
            description: description.trim() || null,
            permissions,
          }),
        saveText: isCreate ? "Create" : "Save",
      }}
    >
      <TextField
        label="Role name"
        fullWidth
        value={name}
        onChange={(e) => setName(e.target.value)}
        autoFocus
      />
      <TextField
        label="Description"
        fullWidth
        multiline
        minRows={2}
        value={description}
        onChange={(e) => setDescription(e.target.value)}
      />
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Permissions ({permissions.length})
        </Typography>
        <FormGroup>
          {ALL_STAFF_PERMISSIONS.map((permission) => (
            <FormControlLabel
              key={permission}
              control={
                <Checkbox
                  checked={permissions.includes(permission)}
                  onChange={() => togglePermission(permission)}
                />
              }
              label={STAFF_PERMISSION_LABELS[permission]}
            />
          ))}
        </FormGroup>
      </Box>
    </Dialog>
  );
}
